import React from 'react';
import { C } from '../../constants/theme';
import { BRIDGE_BAR, PLANNED_RING, TOOTH_COLORS, crownRingTint } from '../../lib/teeth.js';
import {
  crownPath, crownRingPath, fractureGlyph, implantGlyph, postCoreGlyph,
  rootCanalGlyph, veneerArcPath,
} from '../Odontogram/geometry.js';
import TreatedBadge from '../Odontogram/TreatedBadge.jsx';

// The chart's key, for the phone.
//
// The legend is not decoration: the chart encodes clinical meaning in colour
// and glyph, and a colour-coded record with no key is unreadable to anyone but
// its author. But thirteen swatches ahead of the arch would push the chart off
// the fold on a 360px screen, so on mobile it lives inside a collapsed
// `CollapsibleSection` (the screen owns the disclosure; this is only the body).
//
// Every swatch is drawn by the SAME generator the chart uses — never redrawn by
// hand — so the key cannot drift from the chart. That is the desktop legend's
// rule, kept.

const LEGEND_FDI = '16';
const LEGEND_POST_CORE = postCoreGlyph(LEGEND_FDI);

const Item = ({ children, label }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', minWidth: 0 }}>
    {children}
    <span
      style={{
        fontSize: '0.6875rem',
        color: C.textSecondary,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  </div>
);

const Swatch = ({ swatch }) => (
  <span
    aria-hidden
    style={{
      width: '0.75rem',
      height: '0.75rem',
      borderRadius: '0.1875rem',
      display: 'inline-block',
      flex: 'none',
      background: swatch.fill,
      border: `1.5px solid ${swatch.stroke}`,
    }}
  />
);

const Glyph = ({ children }) => (
  <svg
    width={15}
    height={15}
    viewBox="-19 -19 38 38"
    aria-hidden="true"
    style={{ display: 'block', flex: 'none' }}
  >
    {children}
  </svg>
);

const Silhouette = () => (
  <path
    d={crownPath(LEGEND_FDI)}
    fill="none"
    stroke={TOOTH_COLORS.untouched.stroke}
    strokeWidth={1.5}
  />
);

const MobileChartLegend = ({ t }) => (
  <div
    data-testid="chart-legend"
    style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(8rem, 1fr))',
      gap: '0.5rem 0.75rem',
      padding: '0.25rem 0 0.5rem',
    }}
  >
    <Item label={t('chart.surfaceState.caries')}><Swatch swatch={TOOTH_COLORS.caries} /></Item>
    <Item label={t('chart.legend.wholeToothCaries')}><Swatch swatch={TOOTH_COLORS.caries_tooth} /></Item>
    <Item label={t('chart.surfaceState.restoration')}><Swatch swatch={TOOTH_COLORS.restoration} /></Item>
    <Item label={t('chart.surfaceState.recurrent_caries')}><Swatch swatch={TOOTH_COLORS.recurrent_caries} /></Item>
    <Item label={t('chart.surfaceState.temporary')}><Swatch swatch={TOOTH_COLORS.temporary} /></Item>
    <Item label={t('chart.surfaceState.sealant')}><Swatch swatch={TOOTH_COLORS.sealant} /></Item>
    <Item label={t('chart.presence.extracted')}><Swatch swatch={TOOTH_COLORS.missing} /></Item>
    <Item label={t('chart.notCharted')}><Swatch swatch={TOOTH_COLORS.untouched} /></Item>

    <Item label={t('chart.conditionName.crown')}>
      <Glyph>
        <Silhouette />
        <path d={crownRingPath(LEGEND_FDI)} fill="none" stroke={crownRingTint(null)} strokeWidth={2.6} />
      </Glyph>
    </Item>
    <Item label={t('chart.conditionName.root_canal')}>
      <Glyph>
        <Silhouette />
        <polygon points={rootCanalGlyph(LEGEND_FDI)} fill={TOOTH_COLORS.root_canal.stroke} transform="scale(2)" />
      </Glyph>
    </Item>
    <Item label={t('chart.conditionName.implant')}>
      <Glyph>
        <Silhouette />
        <g transform="scale(1.8)">
          {implantGlyph(LEGEND_FDI).map((s, i) => (
            <line
              key={i}
              x1={s.x1} y1={s.y1} x2={s.x2} y2={s.y2}
              stroke={TOOTH_COLORS.implant.stroke}
              strokeWidth={2}
              strokeLinecap="round"
            />
          ))}
        </g>
      </Glyph>
    </Item>
    <Item label={t('chart.conditionName.post_core')}>
      <Glyph>
        <Silhouette />
        <g transform="scale(1.8)">
          <line
            x1={LEGEND_POST_CORE.bar.x1} y1={LEGEND_POST_CORE.bar.y1}
            x2={LEGEND_POST_CORE.bar.x2} y2={LEGEND_POST_CORE.bar.y2}
            stroke={TOOTH_COLORS.post_core.stroke}
            strokeWidth={2}
            strokeLinecap="round"
          />
          <circle
            cx={LEGEND_POST_CORE.dot.cx} cy={LEGEND_POST_CORE.dot.cy} r={LEGEND_POST_CORE.dot.r}
            fill={TOOTH_COLORS.post_core.stroke}
          />
        </g>
      </Glyph>
    </Item>
    <Item label={t('chart.conditionName.veneer')}>
      <Glyph>
        <Silhouette />
        <path
          d={veneerArcPath(LEGEND_FDI)}
          fill="none"
          stroke={TOOTH_COLORS.restoration.stroke}
          strokeWidth={3.5}
          strokeLinecap="round"
        />
      </Glyph>
    </Item>
    <Item label={t('chart.conditionName.fracture')}>
      <Glyph>
        <Silhouette />
        <polyline
          points={fractureGlyph(LEGEND_FDI)}
          fill="none"
          stroke={TOOTH_COLORS.fractured.stroke}
          strokeWidth={2}
        />
      </Glyph>
    </Item>
    {/* The two ANNOTATIONS. Neither is a fact about the tooth — the ring says
        something is PLANNED here, the badge says something was DONE — so they
        close the list rather than sitting among the clinical states. */}
    <Item label={t('chart.legend.planned')}>
      <Glyph>
        <Silhouette />
        <path d={crownPath(LEGEND_FDI)} fill="none" {...PLANNED_RING} />
      </Glyph>
    </Item>
    <Item label={t('chart.legend.treated')}>
      <Glyph>
        <Silhouette />
        <g transform="scale(2.4)">
          <TreatedBadge count={1} />
        </g>
      </Glyph>
    </Item>
    <Item label={t('chart.legend.bridge')}>
      <Glyph>
        {/* The bar has no shape generator — the mark IS a bar along the arch.
            Its stroke RECIPE is still shared: BRIDGE_BAR is the same constant
            the chart spreads. */}
        <line x1={-15} y1={0} x2={15} y2={0} {...BRIDGE_BAR} />
      </Glyph>
    </Item>
  </div>
);

export default MobileChartLegend;
